import { Box, Chip, Option } from "@mui/joy";
import { CloseIcon } from "../../../styles/StyledMUI";
import type {
  EquipmentAttributeDefinition,
  EquipmentModel,
  FilterCriteria,
} from "../../../api/equipment/Equipment.typed";
import { getOperatorLabel, formatEquipmentFilterValueSnippet } from "../../../utils/searchHelpers";
import DarkSelect from "../../common/DarkSelect";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import ActiveAlertChip from "../../common/ActiveAlertChip";
import { colorTokens, withAlpha } from "../../../styles/globalStyles";

interface ActiveCriteriaProps {
  selectedModel: EquipmentModel;
  searchCriteria: FilterCriteria[];
  searchLogic: "AND" | "OR";
  onRemoveCriteria: (criteriaIndex: number) => void;
  onResetAll: () => void;
  onSearchLogicChange: (logic: "AND" | "OR") => void;
  alertName?: string;
  onClearAlertName?: () => void;
}

const chipCloseButtonSx = {
  minWidth: "20px",
  minHeight: "20px",
  p: 0.25,
  zIndex: 10,
  cursor: "pointer",
  "&:hover": { backgroundColor: withAlpha(colorTokens.text.primary, 0.1) },
};

const chipBodySx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  pointerEvents: "none" as const,
};

export default function ActiveCriteria({
  selectedModel,
  searchCriteria,
  searchLogic,
  onRemoveCriteria,
  onResetAll,
  onSearchLogicChange,
  alertName,
  onClearAlertName,
}: ActiveCriteriaProps) {
  const hasAlert = !!alertName;
  const hasCriteria = searchCriteria.length > 0;
  if (!hasAlert && !hasCriteria) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <DarkTypography level="body-sm" colorVariant="muted">
          Filtres actifs:
        </DarkTypography>
        {searchCriteria.length > 1 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DarkTypography level="body-xs" colorVariant="muted">
              Logique:
            </DarkTypography>
            <DarkSelect
              value={searchLogic}
              onChange={(_: any, value: string | null) =>
                onSearchLogicChange(value as "AND" | "OR")
              }
              size="sm"
              sx={{ width: 80 }}
            >
              <Option value="AND">ET</Option>
              <Option value="OR">OU</Option>
            </DarkSelect>
          </Box>
        )}
        <IconButtonCTA
          ctaVariant="danger"
          aria-label="Réinitialiser tous les filtres"
          variant="plain"
          size="sm"
          onClick={onResetAll}
          sx={{
            ml: "auto",
            minWidth: "32px",
            minHeight: "32px",
            color: colorTokens.status.danger,
            "&:hover": { backgroundColor: withAlpha(colorTokens.status.danger, 0.1) },
          }}
        >
          <CloseIcon />
        </IconButtonCTA>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
        {hasAlert && alertName ? (
          <ActiveAlertChip alertName={alertName} onClear={() => onClearAlertName?.()} />
        ) : null}
        {searchCriteria.map((criteria, index) => {
          const attr = selectedModel.Attributes.find(
            (a: EquipmentAttributeDefinition) => a.ID === criteria.AttributeID,
          );

          return (
            <Chip
              key={`${criteria.AttributeID}-${index}`}
              variant="soft"
              color="primary"
              endDecorator={
                <IconButtonCTA
                  ctaVariant="neutral"
                  aria-label="Retirer critère"
                  size="sm"
                  variant="plain"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemoveCriteria(index);
                  }}
                  sx={chipCloseButtonSx}
                >
                  <CloseIcon sx={{ fontSize: "14px" }} />
                </IconButtonCTA>
              }
              sx={{ maxWidth: 300, "& .MuiChip-endDecorator": { pointerEvents: "auto" } }}
            >
              <Box sx={chipBodySx}>
                {attr?.Label || criteria.AttributeID} {getOperatorLabel(criteria.Operator)} «
                {formatEquipmentFilterValueSnippet(criteria)}»
              </Box>
            </Chip>
          );
        })}
      </Box>
    </Box>
  );
}
